import React, { useState, useEffect, useCallback } from 'react';
import { Feature } from "models/api";
import { QueryClient, useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from 'contexts/alertContext';
import { handleError } from 'helpers/utils';
import { addEmployeeEvaluation, getWorkshopEvaluation } from 'framework/employeeWorkshopEvaluation';
import { IEmployeeWorkshopEvaluationDetails } from 'models/employeeWorkshopEvaluationDetails';
import { Button, Card, Grid, Typography } from '@mui/material';

interface Props {
  feature: Feature;
}

const EmployeeWorkshopEvaluationDetails: React.FC<Props> = ({ feature }) => {
  const [evaluationDetails, setEvaluationDetails] = useState<IEmployeeWorkshopEvaluationDetails>({} as IEmployeeWorkshopEvaluationDetails);

  const [loading, setLoading] = useState<boolean>(true);
  const queryClient = new QueryClient();
  const urlParams = useParams();
  const { setAlert } = useAlert();
  let id: string = urlParams[`${feature.singleName}Id`] as string;

  const addEmployeeWorkshopEvaluationMutation = useMutation({
    mutationFn: () => addEmployeeEvaluation(id),
    onSuccess: (data) => {
      navigate(`/employeeWorkshopEvaluation/${id}`);
    }
  });

  const navigate = useNavigate();

  const handleButtonClick = async () => {
    addEmployeeWorkshopEvaluationMutation.mutate();
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await queryClient.fetchQuery(
        "get Workshop Evaluation Details",
        () => getWorkshopEvaluation("/foremployee?id=" + id)
      );
      setEvaluationDetails(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
  }, []);


  if (loading) return <p>Loading Details...</p>;


  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{ borderRadius: 0 }}
        >
          Back
        </Button>
      </div>
      <div style={{ margin: 'auto' }}>
        <Card style={{ padding: '20px', marginBottom: '20px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5" component="div" gutterBottom>
                Workshop Evaluation Details
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Workshop Title (EN):</Typography>
              <Typography variant="body1">{evaluationDetails.workshopTitle}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Workshop Title (AR):</Typography>
              <Typography variant="body1">{evaluationDetails.workshopTitleAr}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Trainer Name (EN):</Typography>
              <Typography variant="body1">{evaluationDetails.trainerName}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Trainer Name (AR):</Typography>
              <Typography variant="body1">{evaluationDetails.trainerNameAr}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">Workshop Date:</Typography>
              <Typography variant="body1">{evaluationDetails.workshopDate}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Number of Questions:</Typography>
              <Typography variant="body1">{evaluationDetails.numberOfQuestions}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Time to Complete (min):</Typography>
              <Typography variant="body1">{evaluationDetails.timeToComplete / 60}</Typography>
            </Grid>
            <Grid item xs={12} style={{ textAlign: 'center', marginTop: '20px' }}>
              {!evaluationDetails.completed ? (
                <Button variant="contained" color="primary" onClick={handleButtonClick}>
                  Proceed
                </Button>
              ) : (
                <h4>You have completed your evaluation</h4>
              )}
            </Grid>

          </Grid>
        </Card>
      </div>

    </div>
  );
};

export default EmployeeWorkshopEvaluationDetails;
